import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
}

interface State {
  crashed: boolean;
}

/**
 * The screen a render-time crash lands on, instead of nothing.
 *
 * React unmounts the entire tree when a render throws and nothing catches it,
 * which paints a blank page: no message, no navigation, no way to sign out. A
 * pending MSP met exactly that, and the only escape was closing the tab or
 * clearing the session by hand.
 *
 * It deliberately holds no product chrome and reads no stores. Whatever failed
 * may be the very thing a nicer screen would need, and a boundary that can
 * itself throw is not a boundary.
 *
 * Recovery is a full reload rather than a state reset: the tree that threw is
 * gone, and anything it had half-built with it.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { crashed: false };

  static getDerivedStateFromError(): State {
    return { crashed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // The console is the only reporting this app has today; without it a crash
    // leaves nothing behind at all.
    console.error("Unhandled render error", error, info.componentStack);
  }

  render() {
    if (!this.state.crashed) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="w-full max-w-md space-y-4 rounded-lg border border-border bg-card p-6 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-md bg-destructive/10 text-destructive">
            <AlertCircle className="h-6 w-6" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">
            Something went wrong
          </h1>
          <p className="text-sm text-muted-foreground">
            The page could not be displayed. Reloading usually clears it — if it
            keeps happening, your administrator can help.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              className="w-full"
              onClick={() => window.location.reload()}
            >
              Reload the page
            </Button>
            {/* Clearing the session first is what makes this an exit rather
                than a loop. `/login` sits behind `GuestGuard`, which sends an
                authenticated visitor straight back to the page they came from —
                the same tree that just threw. A full navigation, not a router
                push, because the router is part of what failed; and the key is
                written literally rather than read from the store, because the
                store may be the thing that broke. */}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                try {
                  localStorage.removeItem("everythingcloud-auth");
                } catch {
                  // Storage unavailable; the navigation below still stands.
                }
                window.location.href = "/login";
              }}
            >
              Back to sign in
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
